import React, { useRef, useState } from "react";
import WebLayout from "../components/WebLayout";
import Seo from "../components/Seo";
import TurnstileWidget from "../components/TurnstileWidget";
import { toast } from "sonner";
import { CheckCircle2, MessageSquare, AlertCircle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website_hp: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState("");
  const [tsToken, setTsToken] = useState("");
  const tsRef = useRef(null);
  const tsRequired = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function handleSubmit(e) {
    e.preventDefault();
    if (tsRequired && !tsToken) { setBanner("Please complete the verification challenge."); return; }
    setLoading(true); setBanner("");
    try {
      const r = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, turnstile_token: tsToken }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 200 && data.status === "ok") {
        setSubmitted(true); toast.success("Message sent.");
      } else if (r.status === 422 && data.status === "turnstile_failed") {
        setBanner("Verification failed. Please try again."); setTsToken(""); tsRef.current?.reset();
      } else if (r.status === 422) { setBanner("Please review the form and try again."); setTsToken(""); tsRef.current?.reset(); }
      else if (r.status === 429) { setBanner("Too many attempts. Please try again later."); setTsToken(""); tsRef.current?.reset(); }
      else { setBanner("Something went wrong. Please try again."); setTsToken(""); tsRef.current?.reset(); }
    } catch { setBanner("Something went wrong. Please try again."); setTsToken(""); tsRef.current?.reset(); }
    finally { setLoading(false); }
  }

  return (
    <WebLayout>
      <Seo title="Contact" description="Get in touch with the SpotBounty team about preregistration, privacy, referrals, or press." path="/contact" />
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="sb-eyebrow flex items-center gap-2"><MessageSquare size={13} className="text-[#FFC107]" /> Get in touch</div>
        <h1 className="sb-headline text-[40px] md:text-[52px] mt-3">CONTACT SPOTBOUNTY</h1>
        <p className="mt-4 text-[#A1A1AA] max-w-xl">Preregistration questions, privacy or data-removal requests, referral-attribution questions, or press inquiries — send us a note and we'll respond.</p>

        {!submitted ? (
          <form onSubmit={handleSubmit} data-testid="contact-form" className="mt-10 sb-card p-6 md:p-8 space-y-4">
            {banner && (
              <div className="sb-card p-3 border-[#EF4444]/40 flex items-start gap-2" style={{ background: "rgba(239,68,68,.06)" }}>
                <AlertCircle size={14} className="text-[#EF4444] mt-0.5" />
                <span className="text-[12.5px] text-[#EF4444]">{banner}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="sb-label">Name</label><input required data-testid="contact-name" className="sb-input" value={form.name} onChange={set("name")} /></div>
              <div><label className="sb-label">Email</label><input required type="email" data-testid="contact-email" className="sb-input" value={form.email} onChange={set("email")} /></div>
            </div>
            <div><label className="sb-label">Subject</label><input data-testid="contact-subject" className="sb-input" value={form.subject} onChange={set("subject")} /></div>
            <div><label className="sb-label">Message</label><textarea required rows={6} data-testid="contact-message" className="sb-input" value={form.message} onChange={set("message")} /></div>
            <input type="text" name="website_hp" value={form.website_hp} onChange={set("website_hp")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            <TurnstileWidget ref={tsRef} onToken={setTsToken} testId="contact-turnstile" />
            <button type="submit" disabled={loading || (tsRequired && !tsToken)} data-testid="contact-submit" className="sb-btn-primary w-full disabled:opacity-50">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send Message"}
            </button>
          </form>
        ) : (
          <div data-testid="contact-confirmation" className="mt-10 sb-card p-10 text-center">
            <CheckCircle2 size={48} className="text-[#FFC107] mx-auto" />
            <div className="mt-5 sb-eyebrow">Received</div>
            <h2 className="text-[28px] font-black mt-3">THANK YOU.</h2>
            <p className="mt-4 text-[#A1A1AA] max-w-md mx-auto">Your message has been recorded. We'll respond to the email you provided as soon as we can.</p>
          </div>
        )}
      </section>
    </WebLayout>
  );
}
